// Statistics.jsx (с AI чатом) - загрузка ключа из .env
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    AreaChart, Area
} from 'recharts';
import './Statistics.css';
import {
    TrendingUp, TrendingDown, Users, CheckCircle,
    Clock, AlertCircle, Package, BarChart2, PieChart as PieChartIcon,
    Bot, Sparkles, Brain, RefreshCw, Key, Download, FileText, MessageSquare, ThumbsUp, ThumbsDown
} from 'lucide-react';

// Мок данные (встроенные в компонент)
const mockOrderData = [
    {
        orderId: "ORD001",
        orderName: "График обмена заданиями 24-24-12",
        orderNumber: "ORD-2025-001",
        status: 1,
        description: "Проектирование и обмен заданиями между отделами",
        operations: [
            {
                operationId: "OP001",
                operationNumber: "OP-001",
                name: "Сводный план сетей_выдача ГПЛ",
                status: 1,
                startDate: "2025-11-05 08:00",
                endDate: "2025-11-05 17:00",
                description: "Выдача сводного плана сетей в отделы",
                resources: [
                    {
                        agentId: "RES001",
                        name: "Иванов Иван",
                        status: 1,
                        type: "Человек",
                        description: "Главный инженер проекта"
                    },
                    {
                        agentId: "MACH001",
                        name: "Рабочая станция ГИП",
                        status: 1,
                        type: "Оборудование",
                        description: "Компьютерная система проектирования"
                    }
                ]
            }
        ]
    },
    {
        orderId: "ORD002",
        orderName: "Теплоизоляция и монтажные чертежи",
        orderNumber: "ORD-2025-002",
        status: 6,
        description: "Работы по теплоизоляции и подготовке монтажных чертежей",
        operations: [
            {
                operationId: "OP004",
                operationNumber: "OP-004",
                name: "Теплоизоляция труб_выдача",
                status: 1,
                startDate: "2025-11-11 09:00",
                endDate: "2025-11-18 16:00",
                description: "Подготовка и выдача документации по теплоизоляции",
                resources: [
                    {
                        agentId: "RES004",
                        name: "Козлова Мария",
                        status: 1,
                        type: "Человек",
                        description: "Инженер теплоснабжения"
                    }
                ]
            }
        ]
    },
    {
        orderId: "ORD003",
        orderName: "Спецификации и готовые разделы",
        orderNumber: "ORD-2025-003",
        status: 3,
        description: "Подготовка спецификаций и завершающих разделов проекта",
        operations: [
            {
                operationId: "OP007",
                operationNumber: "OP-007",
                name: "ОР спецификации_выдача",
                status: 3,
                startDate: "2025-11-22 07:00",
                endDate: "2025-11-22 14:00",
                description: "Выдача основных рабочих спецификаций",
                resources: [
                    {
                        agentId: "RES007",
                        name: "Васильев Сергей",
                        status: 3,
                        type: "Человек",
                        description: "Инженер-сметчик"
                    }
                ]
            }
        ]
    }
];

// API конфигурация
const BASE_URL = 'https://monitor.dev.masiot.dmsolution.ru/api/v1';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

// Получаем API ключ из переменных окружения
const DEEPSEEK_API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY;

// Функция для генерации демо графиков
const getDemoChartHTML = (graphType, chartId, chartTitle = '') => {
    const chartTypes = {
        'orders_trend': 'line',
        'status_distribution': 'doughnut',
        'resource_utilization': 'bar',
        'efficiency_metrics': 'bar',
        'custom': 'line'
    };

    const type = chartTypes[graphType] || 'line';
    const chartContainerId = `chart-${chartId}`;

    let labels = [];
    let dataValues = [];
    let finalChartTitle = chartTitle;

    if (graphType === 'orders_trend') {
        labels = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
        dataValues = [12, 19, 15, 17, 24, 13, 9];
        finalChartTitle = finalChartTitle || 'Динамика заказов';
    } else if (graphType === 'status_distribution') {
        labels = ['Утверждено', 'В работе', 'На рассмотрении', 'Отменено'];
        dataValues = [30, 45, 15, 10];
        finalChartTitle = finalChartTitle || 'Распределение статусов';
    } else if (graphType === 'resource_utilization') {
        labels = ['Иванов', 'Петров', 'Сидоров', 'Станок А', 'Станок Б'];
        dataValues = [85, 92, 78, 65, 88];
        finalChartTitle = finalChartTitle || 'Загрузка ресурсов';
    } else if (graphType === 'efficiency_metrics') {
        labels = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн'];
        dataValues = [78, 82, 85, 79, 88, 92];
        finalChartTitle = finalChartTitle || 'Метрики эффективности';
    } else {
        labels = ['Категория 1', 'Категория 2', 'Категория 3', 'Категория 4'];
        dataValues = [30, 45, 15, 10];
        finalChartTitle = finalChartTitle || 'Пользовательский график';
    }

    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${finalChartTitle}</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
            background: #ffffff;
            margin: 0;
            padding: 10px;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .chart-wrapper {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
        }
        .chart-header {
            text-align: center;
            margin-bottom: 10px;
            font-size: 14px;
            font-weight: 600;
            color: #1f2937;
        }
        .chart-container { 
            position: relative; 
            height: 250px; 
            width: 100%;
            margin: 0 auto;
        }
        canvas {
            display: block;
            width: 100% !important;
            height: 100% !important;
        }
    </style>
</head>
<body>
    <div class="chart-wrapper">
        <div class="chart-header">${finalChartTitle}</div>
        <div class="chart-container">
            <canvas id="${chartContainerId}"></canvas>
        </div>
    </div>
    <script>
        (function() {
            try {
                function initChart() {
                    const canvas = document.getElementById('${chartContainerId}');
                    if (!canvas) {
                        console.error('Canvas not found');
                        return;
                    }
                    
                    const ctx = canvas.getContext('2d');
                    
                    const labels = ${JSON.stringify(labels)};
                    const dataValues = ${JSON.stringify(dataValues)};
                    
                    const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];
                    
                    let config = {
                        type: '${type}',
                        data: {
                            labels: labels,
                            datasets: [{
                                label: 'Значение',
                                data: dataValues,
                                backgroundColor: '${type}' === 'line' ? colors[0] : colors.slice(0, dataValues.length),
                                borderColor: '${type}' === 'line' ? colors[0] : '#ffffff',
                                borderWidth: 2,
                                tension: 0.3
                            }]
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { 
                                    position: 'top',
                                    labels: { 
                                        font: { size: 11 },
                                        color: '#1f2937',
                                        boxWidth: 12
                                    }
                                },
                                tooltip: {
                                    backgroundColor: '#1f2937',
                                    titleColor: '#f3f4f6',
                                    bodyColor: '#e5e7eb',
                                    padding: 8,
                                    titleFont: { size: 12 },
                                    bodyFont: { size: 11 }
                                }
                            }
                        }
                    };
                    
                    if ('${type}' !== 'pie' && '${type}' !== 'doughnut') {
                        config.options.scales = {
                            y: {
                                beginAtZero: true,
                                grid: { color: '#e5e7eb', lineWidth: 0.5 },
                                ticks: { color: '#6b7280', font: { size: 10 } }
                            },
                            x: {
                                grid: { display: false },
                                ticks: { color: '#6b7280', font: { size: 10 } }
                            }
                        };
                    }
                    
                    if ('${type}' === 'doughnut') {
                        config.type = 'doughnut';
                    }
                    
                    new Chart(ctx, config);
                }

                if (document.readyState === 'loading') {
                    document.addEventListener('DOMContentLoaded', initChart);
                } else {
                    initChart();
                }
            } catch (error) {
                console.error('Chart error:', error);
                document.body.innerHTML = '<div style="color: red; padding: 10px; text-align: center;">Ошибка загрузки графика</div>';
            }
        })();
    </script>
</body>
</html>`;
};

// Функция для сохранения HTML в файл
const saveChartToFile = (html, chartName) => {
    try {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
        const safeChartName = chartName.replace(/[^a-zа-яё0-9]/gi, '_').toLowerCase();
        const fileName = `statgragh_${safeChartName}_${timestamp}.html`;

        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return true;
    } catch (error) {
        console.error('Error saving chart:', error);
        return false;
    }
};

// Функция для анализа запроса через DeepSeek API
const analyzeWithDeepSeek = async (prompt, stats) => {
    try {
        const systemPrompt = `Ты - эксперт по анализу данных и визуализации. 
Проанализируй запрос пользователя и предложи подходящие типы графиков на основе доступных данных.

Доступные данные:
- Заказы: всего ${stats.orders.total || 0}, в работе ${stats.orders.inProgress || 0}, завершено ${stats.orders.completed || 0}
- Операции: всего ${stats.operations.total || 0}
- Ресурсы: всего ${stats.resources.total || 0}, активных ${stats.resources.active || 0}

Ответ должен быть в формате JSON:
{
    "interpretation": "краткое описание того, как ты понял запрос",
    "confidence": число от 0 до 1,
    "suggestions": [
        {
            "type": "тип_графика",
            "name": "название",
            "description": "описание",
            "reason": "почему этот график подходит"
        }
    ],
    "message": "сообщение для пользователя"
}

Доступные типы графиков:
- orders_trend - линейный график тренда заказов
- status_distribution - круговая диаграмма статусов
- resource_utilization - столбчатая диаграмма загрузки ресурсов
- efficiency_metrics - гистограмма метрик эффективности
- custom - пользовательский график

Предложи 2-3 наиболее подходящих варианта.`;

        const response = await fetch(DEEPSEEK_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
            },
            body: JSON.stringify({
                model: 'deepseek-chat',
                messages: [
                    { role: 'system', content: systemPrompt },
                    { role: 'user', content: prompt }
                ],
                temperature: 0.7,
                max_tokens: 1000
            })
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        const content = data.choices[0].message.content;

        try {
            const jsonMatch = content.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                return JSON.parse(jsonMatch[0]);
            }
        } catch (e) {
            console.error('Failed to parse AI response:', e);
        }

        return {
            interpretation: "Я проанализировал ваш запрос",
            confidence: 0.5,
            suggestions: [
                {
                    type: "orders_trend",
                    name: "📈 Тренд заказов",
                    description: "Динамика количества заказов",
                    reason: "Базовый график для анализа"
                },
                {
                    type: "status_distribution",
                    name: "🍩 Распределение статусов",
                    description: "Текущее состояние заказов",
                    reason: "Общая картина по статусам"
                }
            ],
            message: "Я не совсем уверен в понимании. Вот несколько базовых вариантов:"
        };
    } catch (error) {
        console.error('DeepSeek API error:', error);
        throw error;
    }
};

// Компонент для отображения графика в маленьком окошке
const ChartWidget = ({ html, id }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (containerRef.current && html) {
            containerRef.current.innerHTML = '';

            const iframe = document.createElement('iframe');
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.border = 'none';
            iframe.style.background = 'white';
            iframe.style.borderRadius = '8px';

            containerRef.current.appendChild(iframe);

            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(html);
            iframeDoc.close();
        }
    }, [html]);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                height: '300px',
                background: '#f9fafb',
                borderRadius: '8px',
                overflow: 'hidden'
            }}
        />
    );
};

// Компонент для отображения результатов анализа
const AnalysisResult = ({ analysis, onAccept, onReject, onSelectSuggestion, onModify, isLoading }) => {
    if (!analysis && !isLoading) return null;

    if (isLoading) {
        return (
            <div className="ai-analysis-result loading">
                <div className="ai-analysis-header">
                    <Brain size={20} className="pulse" />
                    <span>AI анализирует запрос...</span>
                </div>
                <div className="ai-analysis-loading">
                    <div className="loading-spinner"></div>
                    <p>Думаю над лучшими вариантами графиков</p>
                </div>
            </div>
        );
    }

    return (
        <div className="ai-analysis-result">
            <div className="ai-analysis-header">
                <MessageSquare size={20} />
                <span>AI анализ запроса</span>
                {analysis.confidence && (
                    <span className="ai-confidence">
                        Уверенность: {Math.round(analysis.confidence * 100)}%
                    </span>
                )}
            </div>

            {analysis.interpretation && (
                <div className="ai-interpretation">
                    <strong>Я понял:</strong> {analysis.interpretation}
                </div>
            )}

            <div className="ai-analysis-message">
                {analysis.message}
            </div>

            {analysis.suggestions && analysis.suggestions.length > 0 && (
                <div className="ai-suggestions">
                    <div className="ai-suggestions-title">Предлагаемые варианты графиков:</div>
                    <div className="ai-suggestions-list">
                        {analysis.suggestions.map((suggestion, index) => (
                            <button
                                key={index}
                                className="ai-suggestion-btn"
                                onClick={() => onSelectSuggestion(suggestion.type, suggestion.name)}
                            >
                                <div className="ai-suggestion-name">{suggestion.name}</div>
                                <div className="ai-suggestion-desc">{suggestion.description}</div>
                                {suggestion.reason && (
                                    <div className="ai-suggestion-reason">💡 {suggestion.reason}</div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="ai-analysis-actions">
                {analysis.suggestions && analysis.suggestions.length > 0 && (
                    <button
                        className="ai-analysis-accept"
                        onClick={() => onAccept(analysis.suggestions[0].type)}
                    >
                        <ThumbsUp size={16} />
                        Создать первый вариант
                    </button>
                )}
                <button
                    className="ai-analysis-reject"
                    onClick={onReject}
                >
                    <ThumbsDown size={16} />
                    Не подходит
                </button>
                <button
                    className="ai-analysis-modify"
                    onClick={onModify}
                >
                    <RefreshCw size={16} />
                    Уточнить запрос
                </button>
            </div>
        </div>
    );
};

// Основной компонент Statistics
function Statistics() {
    const [stats, setStats] = useState({
        orders: {},
        operations: {},
        resources: {},
        loading: true,
        error: null
    });

    const [timeRange, setTimeRange] = useState('week');
    const [dataSource, setDataSource] = useState('demo');
    const [aiCharts, setAiCharts] = useState([]);
    const [customPrompt, setCustomPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [saveToFile, setSaveToFile] = useState(true);

    const [analysisResult, setAnalysisResult] = useState(null);
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [pendingPrompt, setPendingPrompt] = useState('');

    const [hasApiKey, setHasApiKey] = useState(!!DEEPSEEK_API_KEY);

    const chartTemplates = [
        {
            id: 'orders_trend',
            name: '📈 Тренд заказов',
            description: 'Динамика количества заказов за период',
            icon: '📈'
        },
        {
            id: 'status_distribution',
            name: '🍩 Распределение статусов',
            description: 'Круговая диаграмма статусов заказов',
            icon: '🍩'
        },
        {
            id: 'resource_utilization',
            name: '📊 Использование ресурсов',
            description: 'Загрузка оборудования и персонала',
            icon: '📊'
        },
        {
            id: 'efficiency_metrics',
            name: '⚡ Метрики эффективности',
            description: 'KPI и показатели производительности',
            icon: '⚡'
        }
    ];

    const createDemoData = useCallback(() => {
        return {
            orders: mockOrderData.map(order => ({
                id: order.orderId,
                status: order.status,
                createdAt: new Date().toISOString(),
                name: order.orderName,
                operations: order.operations
            })),
            operations: mockOrderData.flatMap(order =>
                order.operations.map(op => ({
                    id: op.operationId,
                    type: op.name,
                    status: op.status,
                    startDate: op.startDate,
                    endDate: op.endDate
                }))
            ),
            resources: mockOrderData.flatMap(order =>
                order.operations.flatMap(op =>
                    op.resources.map(res => ({
                        id: res.agentId,
                        typeAgent: res.type,
                        status: res.status,
                        name: res.name
                    }))
                )
            )
        };
    }, []);

    const getStatusName = useCallback((status) => {
        const statusMap = {
            1: 'Утверждено',
            2: 'Завершено',
            3: 'Забронировано',
            4: 'Отменено',
            5: 'Готов к выполнению',
            6: 'В работе',
            7: 'На рассмотрении',
            10: 'Утверждено'
        };
        return statusMap[status] || 'Неизвестно';
    }, []);

    const getStatusColor = useCallback((status) => {
        const colorMap = {
            1: '#10b981',
            2: '#6b7280',
            3: '#f59e0b',
            4: '#ef4444',
            5: '#eab308',
            6: '#3b82f6',
            7: '#8b5cf6',
            10: '#10b981'
        };
        return colorMap[status] || '#9ca3af';
    }, []);

    const processOrdersData = useCallback((orders) => {
        if (!orders || !Array.isArray(orders)) return {
            total: 0,
            completed: 0,
            inProgress: 0,
            pending: 0,
            canceled: 0,
            statusDistribution: [],
            dailyTrend: []
        };

        const statusStats = orders.reduce((acc, order) => {
            const status = order.status || 0;
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const dateStats = {};
        const today = new Date();
        for (let i = 6; i >= 0; i--) {
            const date = new Date(today.getTime() - i * 86400000);
            const dateStr = date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
            dateStats[dateStr] = Math.floor(Math.random() * 10) + 5;
        }

        return {
            total: orders.length,
            completed: orders.filter(o => o.status === 2 || o.status === 10).length,
            inProgress: orders.filter(o => o.status === 6).length,
            pending: orders.filter(o => o.status === 7 || o.status === 3).length,
            canceled: orders.filter(o => o.status === 4).length,
            statusDistribution: Object.entries(statusStats).map(([status, count]) => ({
                name: getStatusName(parseInt(status)),
                value: count,
                status: parseInt(status)
            })),
            dailyTrend: Object.entries(dateStats).map(([date, count]) => ({
                date,
                orders: count
            }))
        };
    }, [getStatusName]);

    const processOperationsData = useCallback((operations) => {
        if (!operations || !Array.isArray(operations)) return {
            total: 0,
            recent: 0,
            avgDuration: 0,
            byType: {},
            efficiency: 0
        };

        return {
            total: operations.length,
            recent: Math.floor(operations.length * 0.7),
            avgDuration: 4.5,
            byType: operations.reduce((acc, op) => {
                const type = op.type || 'Не указан';
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {}),
            efficiency: Math.floor(Math.random() * 30) + 70
        };
    }, []);

    const processResourcesData = useCallback((resources) => {
        if (!resources || !Array.isArray(resources)) return {
            total: 0,
            active: 0,
            utilization: 0,
            byType: {},
            availability: 0
        };

        return {
            total: resources.length,
            active: resources.filter(r => r.status === 1 || r.status === 6).length,
            utilization: Math.floor(Math.random() * 40) + 50,
            byType: resources.reduce((acc, res) => {
                const type = res.typeAgent || 'Не указан';
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {}),
            availability: 80
        };
    }, []);

    const fetchDemoData = useCallback(async () => {
        return createDemoData();
    }, [createDemoData]);

    const fetchApiData = useCallback(async () => {
        try {
            const [ordersResponse, operationsResponse, resourcesResponse] = await Promise.all([
                fetch(`${BASE_URL}/orders/`).then(res => res.ok ? res.json() : []).catch(() => []),
                fetch(`${BASE_URL}/operations/`).then(res => res.ok ? res.json() : []).catch(() => []),
                fetch(`${BASE_URL}/resources/`).then(res => res.ok ? res.json() : []).catch(() => [])
            ]);

            return {
                orders: ordersResponse || [],
                operations: operationsResponse || [],
                resources: resourcesResponse || []
            };
        } catch (error) {
            console.error('API fetch error:', error);
            return fetchDemoData();
        }
    }, [fetchDemoData]);

    const loadStatisticsData = useCallback(async () => {
        try {
            setStats(prev => ({ ...prev, loading: true }));

            let data;

            if (dataSource === 'demo') {
                data = await fetchDemoData();
            } else {
                data = await fetchApiData();
            }

            const processedOrders = processOrdersData(data.orders);
            const processedOperations = processOperationsData(data.operations);
            const processedResources = processResourcesData(data.resources);

            setStats({
                orders: processedOrders,
                operations: processedOperations,
                resources: processedResources,
                loading: false,
                error: null
            });
        } catch (error) {
            console.error('Error loading statistics:', error);
            setStats(prev => ({
                ...prev,
                loading: false,
                error: 'Не удалось загрузить данные'
            }));
        }
    }, [dataSource, fetchDemoData, fetchApiData, processOrdersData, processOperationsData, processResourcesData]);

    useEffect(() => {
        loadStatisticsData();
    }, [loadStatisticsData]);

    const handleDataSourceChange = (source) => {
        setDataSource(source);
    };

    const handlePromptSubmit = async (e) => {
        e.preventDefault();
        if (!customPrompt.trim()) return;

        if (!hasApiKey) {
            alert('API ключ не найден в файле .env. Пожалуйста, добавьте REACT_APP_DEEPSEEK_API_KEY в .env файл');
            return;
        }

        setIsAnalyzing(true);
        setShowAnalysis(true);
        setPendingPrompt(customPrompt);

        try {
            const analysis = await analyzeWithDeepSeek(customPrompt, stats);
            setAnalysisResult(analysis);
        } catch (error) {
            console.error('Analysis error:', error);
            alert('Ошибка при анализе запроса. Проверьте API ключ в .env файле и попробуйте снова.');
            setShowAnalysis(false);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleAcceptSuggestion = (graphType) => {
        setShowAnalysis(false);
        generateAIChart(graphType, pendingPrompt);
    };

    const handleSelectSuggestion = (graphType, graphName) => {
        setShowAnalysis(false);
        generateAIChart(graphType, pendingPrompt);
    };

    const handleRejectSuggestion = () => {
        setShowAnalysis(false);
        setAnalysisResult(null);
        setPendingPrompt('');
    };

    const handleModifyRequest = () => {
        setShowAnalysis(false);
        setAnalysisResult(null);
    };

    const generateAIChart = async (graphType, userPrompt) => {
        setIsGenerating(true);
        try {
            const chartId = Date.now();

            let chartTitle = userPrompt;
            if (userPrompt.length > 50) {
                chartTitle = userPrompt.substring(0, 47) + '...';
            }

            const htmlChart = getDemoChartHTML(graphType, chartId, chartTitle);

            const chartName = chartTemplates.find(t => t.id === graphType)?.name || 'AI График';

            const newChart = {
                id: chartId,
                html: htmlChart,
                name: chartName,
                userPrompt: userPrompt,
                timestamp: new Date().toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                date: new Date().toLocaleDateString('ru-RU'),
                source: dataSource === 'demo' ? 'DEMO' : 'API',
                type: graphType
            };

            if (saveToFile) {
                const saved = saveChartToFile(htmlChart, chartName + ' - ' + userPrompt.substring(0, 30));
                if (saved) {
                    newChart.savedToFile = true;
                }
            }

            setAiCharts(prev => [newChart, ...prev]);
            setCustomPrompt('');
            setPendingPrompt('');
        } catch (error) {
            console.error('Error generating AI chart:', error);
            alert('Ошибка при генерации графика.');
        } finally {
            setIsGenerating(false);
        }
    };

    const removeAIChart = (chartId) => {
        setAiCharts(prev => prev.filter(chart => chart.id !== chartId));
    };

    const handleSaveChartAgain = (chart) => {
        saveChartToFile(chart.html, chart.name + ' - ' + (chart.userPrompt || '').substring(0, 30));
    };

    const kpiData = [
        {
            name: 'Заказы',
            value: stats.orders.total || 0,
            icon: <Package size={24} />,
            change: '+12%',
            trend: 'up',
            color: '#3b82f6'
        },
        {
            name: 'Операции',
            value: stats.operations.total || 0,
            icon: <BarChart2 size={24} />,
            change: '+8%',
            trend: 'up',
            color: '#8b5cf6'
        },
        {
            name: 'Ресурсы',
            value: stats.resources.total || 0,
            icon: <Users size={24} />,
            change: '+5%',
            trend: 'up',
            color: '#10b981'
        },
        {
            name: 'Эффективность',
            value: `${stats.operations.efficiency || 0}%`,
            icon: <CheckCircle size={24} />,
            change: '+3%',
            trend: 'up',
            color: '#f59e0b'
        }
    ];

    if (stats.loading) {
        return (
            <div className="stat-screen">
                <div className="stat-loading">
                    <div className="stat-loading-spinner"></div>
                    <p>Загрузка статистики...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="stat-screen">
            <div className="stat-content">
                <div className="stat-header">
                    <div className="stat-header-left">
                        <h1>📊 Статистика системы</h1>
                        <p>Аналитика и метрики производительности</p>
                    </div>

                    <div className="stat-header-controls">
                        <div className="stat-data-source-selector">
                            <button
                                className={`stat-data-source-btn ${dataSource === 'demo' ? 'active' : ''}`}
                                onClick={() => handleDataSourceChange('demo')}
                                style={{
                                    background: dataSource === 'demo' ? '#ff7675' : '#f1f3f4',
                                    color: dataSource === 'demo' ? 'white' : '#5f6368'
                                }}
                            >
                                <span className="stat-data-source-icon">📊</span>
                                DEMO
                                {dataSource === 'demo' && <span className="stat-active-badge">активно</span>}
                            </button>
                            <button
                                className={`stat-data-source-btn ${dataSource === 'api' ? 'active' : ''}`}
                                onClick={() => handleDataSourceChange('api')}
                                style={{
                                    background: dataSource === 'api' ? '#74b9ff' : '#f1f3f4',
                                    color: dataSource === 'api' ? 'white' : '#5f6368'
                                }}
                            >
                                <span className="stat-data-source-icon">🔄</span>
                                API
                                {dataSource === 'api' && <span className="stat-active-badge">активно</span>}
                            </button>
                        </div>

                        <div className="stat-time-range-selector">
                            <button
                                className={`stat-time-btn ${timeRange === 'day' ? 'active' : ''}`}
                                onClick={() => setTimeRange('day')}
                            >
                                День
                            </button>
                            <button
                                className={`stat-time-btn ${timeRange === 'week' ? 'active' : ''}`}
                                onClick={() => setTimeRange('week')}
                            >
                                Неделя
                            </button>
                            <button
                                className={`stat-time-btn ${timeRange === 'month' ? 'active' : ''}`}
                                onClick={() => setTimeRange('month')}
                            >
                                Месяц
                            </button>
                        </div>

                        <button className="stat-refresh-btn" onClick={loadStatisticsData}>
                            <RefreshCw size={18} />
                            Обновить
                        </button>
                    </div>
                </div>

                <div className="stat-data-source-indicator">
                    <div className={`stat-indicator-dot ${dataSource === 'demo' ? 'demo' : 'api'}`}></div>
                    <span>
                        Режим: <strong>{dataSource === 'demo' ? 'ДЕМО-ДАННЫЕ' : 'API (реальные данные)'}</strong>
                    </span>
                    {!hasApiKey && (
                        <span className="stat-api-key-warning">
                            ⚠️ API ключ не найден в .env файле
                        </span>
                    )}
                </div>

                <div className="stat-kpi-grid">
                    {kpiData.map((kpi, index) => (
                        <div key={index} className="stat-kpi-card" style={{ borderLeftColor: kpi.color }}>
                            <div className="stat-kpi-header">
                                <div className="stat-kpi-icon" style={{ color: kpi.color }}>
                                    {kpi.icon}
                                </div>
                                <span className="stat-kpi-trend">
                                    {kpi.trend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                                    {kpi.change}
                                </span>
                            </div>
                            <h3 className="stat-kpi-value">{kpi.value}</h3>
                            <p className="stat-kpi-label">{kpi.name}</p>
                        </div>
                    ))}
                </div>

                <div className="stat-charts-section">
                    <div className="stat-chart-card">
                        <div className="stat-chart-header">
                            <h3><BarChart2 size={20} /> Динамика заказов</h3>
                            <span className="stat-chart-subtitle">За последнюю неделю</span>
                        </div>
                        <div className="stat-chart-container">
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={stats.orders.dailyTrend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="date" stroke="#6b7280" />
                                    <YAxis stroke="#6b7280" />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: '8px',
                                            border: '1px solid #e5e7eb',
                                            background: 'white'
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="orders"
                                        stroke="#3b82f6"
                                        fill="#93c5fd"
                                        strokeWidth={2}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="stat-chart-card">
                        <div className="stat-chart-header">
                            <h3><PieChartIcon size={20} /> Распределение статусов</h3>
                            <span className="stat-chart-subtitle">Все заказы</span>
                        </div>
                        <div className="stat-chart-container">
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={stats.orders.statusDistribution || []}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {(stats.orders.statusDistribution || []).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="stat-detailed-stats">
                    <div className="stat-stats-card">
                        <div className="stat-stats-header">
                            <h3><Users size={20} /> Статистика ресурсов</h3>
                        </div>
                        <div className="stat-stats-content">
                            <div className="stat-stats-row">
                                <span>Всего ресурсов</span>
                                <span className="stat-stats-value">{stats.resources.total}</span>
                            </div>
                            <div className="stat-stats-row">
                                <span>Активных</span>
                                <span className="stat-stats-value" style={{ color: '#10b981' }}>
                                    {stats.resources.active}
                                </span>
                            </div>
                            <div className="stat-stats-row">
                                <span>Загрузка</span>
                                <span className="stat-stats-value">{stats.resources.utilization}%</span>
                            </div>
                            <div className="stat-stats-row">
                                <span>Доступность</span>
                                <span className="stat-stats-value">{stats.resources.availability}%</span>
                            </div>
                        </div>
                    </div>

                    <div className="stat-stats-card">
                        <div className="stat-stats-header">
                            <h3><Clock size={20} /> Эффективность операций</h3>
                        </div>
                        <div className="stat-stats-content">
                            <div className="stat-stats-row">
                                <span>Всего операций</span>
                                <span className="stat-stats-value">{stats.operations.total}</span>
                            </div>
                            <div className="stat-stats-row">
                                <span>Средняя длительность</span>
                                <span className="stat-stats-value">{stats.operations.avgDuration?.toFixed(1)} ч</span>
                            </div>
                            <div className="stat-stats-row">
                                <span>Эффективность</span>
                                <span className="stat-stats-value" style={{ color: '#10b981' }}>
                                    {stats.operations.efficiency}%
                                </span>
                            </div>
                            <div className="stat-stats-row">
                                <span>За неделю</span>
                                <span className="stat-stats-value">{stats.operations.recent}</span>
                            </div>
                        </div>
                    </div>

                    <div className="stat-stats-card">
                        <div className="stat-stats-header">
                            <h3><AlertCircle size={20} /> Статусы заказов</h3>
                        </div>
                        <div className="stat-stats-content">
                            <div className="stat-stats-row">
                                <span>Всего заказов</span>
                                <span className="stat-stats-value">{stats.orders.total}</span>
                            </div>
                            <div className="stat-stats-row">
                                <span>Завершено</span>
                                <span className="stat-stats-value" style={{ color: '#10b981' }}>
                                    {stats.orders.completed}
                                </span>
                            </div>
                            <div className="stat-stats-row">
                                <span>В работе</span>
                                <span className="stat-stats-value" style={{ color: '#f59e0b' }}>
                                    {stats.orders.inProgress}
                                </span>
                            </div>
                            <div className="stat-stats-row">
                                <span>На рассмотрении</span>
                                <span className="stat-stats-value" style={{ color: '#8b5cf6' }}>
                                    {stats.orders.pending}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="stat-additional-charts">
                    <div className="stat-chart-card full-width">
                        <div className="stat-chart-header">
                            <h3>📈 Сравнительный анализ</h3>
                            <span className="stat-chart-subtitle">Производительность по типам ресурсов</span>
                        </div>
                        <div className="stat-chart-container">
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={Object.entries(stats.resources.byType || {}).map(([type, count]) => ({
                                    type: type.length > 15 ? type.substring(0, 15) + '...' : type,
                                    count
                                }))}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="type" angle={-45} textAnchor="end" height={80} stroke="#6b7280" />
                                    <YAxis stroke="#6b7280" />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="count" name="Количество ресурсов" fill="#8884d8" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="ai-charts-section">
                    <div className="ai-charts-header">
                        <div className="ai-charts-title">
                            <Bot size={24} />
                            <h2>🤖 AI Генератор графиков</h2>
                        </div>
                        <div className="ai-charts-subtitle">
                            Опишите какой график вам нужен, и AI поможет его создать
                            <div className="ai-charts-controls">
                                <label className="ai-save-toggle">
                                    <input
                                        type="checkbox"
                                        checked={saveToFile}
                                        onChange={(e) => setSaveToFile(e.target.checked)}
                                    />
                                    <FileText size={14} />
                                    Сохранять в файл
                                </label>
                                <div className="ai-api-key-status">
                                    {hasApiKey ? (
                                        <span className="ai-api-key-valid">✅ API ключ загружен</span>
                                    ) : (
                                        <span className="ai-api-key-invalid">❌ API ключ не найден в .env</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ai-generator-card">
                        <div className="ai-generator-header">
                            <Sparkles size={20} />
                            <h3>Создать новый график</h3>
                            <div className="ai-api-status">
                                {hasApiKey ? (
                                    <span className="ai-api-active">API ключ активен</span>
                                ) : (
                                    <span className="ai-api-demo">Демо режим (без AI)</span>
                                )}
                            </div>
                        </div>

                        <form className="ai-prompt-input" onSubmit={handlePromptSubmit}>
                            <input
                                type="text"
                                value={customPrompt}
                                onChange={(e) => setCustomPrompt(e.target.value)}
                                placeholder="Опишите какой график хотите создать (например: 'покажи тренд заказов за последнюю неделю')..."
                                disabled={isGenerating || isAnalyzing || !hasApiKey}
                            />
                            <button
                                type="submit"
                                className="ai-generate-btn"
                                disabled={isGenerating || isAnalyzing || !customPrompt.trim() || !hasApiKey}
                            >
                                {isAnalyzing ? (
                                    <>
                                        <RefreshCw size={16} className="spin" />
                                        Анализ...
                                    </>
                                ) : isGenerating ? (
                                    <>
                                        <RefreshCw size={16} className="spin" />
                                        Генерация...
                                    </>
                                ) : (
                                    <>
                                        <Brain size={16} />
                                        Анализировать
                                    </>
                                )}
                            </button>
                        </form>

                        {showAnalysis && hasApiKey && (
                            <AnalysisResult
                                analysis={analysisResult}
                                isLoading={isAnalyzing}
                                onAccept={handleAcceptSuggestion}
                                onReject={handleRejectSuggestion}
                                onSelectSuggestion={handleSelectSuggestion}
                                onModify={handleModifyRequest}
                            />
                        )}

                        {!showAnalysis && (
                            <div className="ai-templates-grid">
                                {chartTemplates.map(template => (
                                    <button
                                        key={template.id}
                                        className="ai-template-btn"
                                        onClick={() => generateAIChart(template.id, template.name)}
                                        disabled={isGenerating}
                                    >
                                        <span className="ai-template-icon">{template.icon}</span>
                                        <div className="ai-template-info">
                                            <div className="ai-template-name">{template.name}</div>
                                            <div className="ai-template-desc">{template.description}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {aiCharts.length > 0 && (
                        <div className="ai-generated-charts">
                            <h3>Сгенерированные графики ({aiCharts.length})</h3>
                            <div className="ai-charts-grid">
                                {aiCharts.map(chart => (
                                    <div key={chart.id} className="ai-chart-card">
                                        <div className="ai-chart-header">
                                            <div className="ai-chart-title">
                                                <span>{chart.name}</span>
                                                <span className="ai-chart-time">
                                                    {chart.date} {chart.timestamp} • {chart.source}
                                                    {chart.userPrompt && (
                                                        <span className="ai-chart-prompt" title={chart.userPrompt}>
                                                            {" • "}{chart.userPrompt.substring(0, 30)}...
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="ai-chart-actions">
                                                {chart.savedToFile && (
                                                    <span className="ai-chart-saved" title="Сохранено в файл">
                                                        <FileText size={14} />
                                                    </span>
                                                )}
                                                <button
                                                    className="ai-chart-save"
                                                    onClick={() => handleSaveChartAgain(chart)}
                                                    title="Сохранить в файл"
                                                >
                                                    <Download size={14} />
                                                </button>
                                                <button
                                                    className="ai-chart-remove"
                                                    onClick={() => removeAIChart(chart.id)}
                                                    title="Удалить график"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                        <div className="ai-chart-content" style={{ height: '300px', padding: '10px' }}>
                                            <ChartWidget html={chart.html} id={chart.id} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {stats.error && (
                    <div className="stat-error-message">
                        <AlertCircle size={24} />
                        <span>{stats.error}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Statistics;